/**
 * Typed route registry: single source of truth for every URL in the app.
 *
 * Static routes are plain strings; dynamic routes are builder functions so the
 * param is impossible to forget or misplace.
 *
 * The SAME builder serves both navigation and routing:
 *   navigate(paths.admin.lists.detail(list.id))   // "/admin/collection/lic-42"
 *   <Route path={paths.admin.lists.detail(':id')} />  // "/admin/collection/:id"
 * Call a builder with a real id to navigate, or with ':id' to get the
 * React Router pattern: one definition, zero drift.
 */

export const paths = {
  // ─── Public ─────────────────────────────────────────────────────────
  login: '/login',
  forgotPassword: '/forgot-password',
  resetPassword: '/reset-password',
  // Public because the link is opened from the NEW mailbox, by someone who has no
  // session, and who, once the change commits, cannot have one: every session is
  // revoked. Must match the URL the backend mails (`email-change.routes.ts`).
  confirmEmailChange: '/confirm-email-change',
  onboarding: '/onboarding',

  // ─── Client ─────────────────────────────────────────────────────────
  home: '/',
  account: '/account',
  licence: '/licence',
  lists: {
    root: '/lists',
    detail: (id: string) => `/lists/${id}`,
    export: (id: string) => `/lists/${id}/export`,
  },
  institutionals: {
    root: '/institutionals',
    detail: (id: string) => `/institutionals/${id}`,
  },
  schools: '/schools',
  profiles: {
    all: '/profiles/all',
    export: '/profiles/export',
    detail: (id: string) => `/profiles/${id}`,
  },
  marketplace: '/marketplace',
  imports: {
    root: '/imports',
    new: '/imports/new',
  },
  trackedLps: '/tracked-lps',
  fundraisings: {
    root: '/fundraisings',
    /** The page with a dialog already open: `'new'`, or the id of the round to edit (#668). */
    dialog: (round: 'new' | (string & {})) => `/fundraisings?round=${encodeURIComponent(round)}`,
  },
  /** The clients' FAQ (#735); `#<question id>` opens one question. */
  faq: '/faq',

  // ─── Admin ──────────────────────────────────────────────────────────
  admin: {
    licences: {
      root: '/admin/licences',
      detail: (id: string) => `/admin/licences/${id}`,
    },
    // No `add` / `edit` for either: creating and editing are dialogs the pages
    // open from their own state, so there is no URL to name.
    users: {
      root: '/admin/users',
    },
    administrators: {
      root: '/admin/administrators',
    },
    // The admin zone calls the feature "Collection" (#548) and so does its URL,
    // so the address bar matches the nav. The key stays `lists`: the entity is
    // still a list, and so are the backend resource and the feature folder.
    lists: {
      root: '/admin/collection',
      add: '/admin/collection/add',
      detail: (id: string) => `/admin/collection/${id}`,
      edit: (id: string) => `/admin/collection/${id}/edit`,
      fileImport: (id: string) => `/admin/collection/${id}/file-import`,
      export: (id: string) => `/admin/collection/${id}/export`,
    },
    /** The client comments on lists (#717); `?feedback=<id>` opens one. */
    listFeedbacks: '/admin/list-feedbacks',
    companies: {
      root: '/admin/companies',
      detail: (id: string) => `/admin/companies/${id}`,
    },
    schools: '/admin/schools',
    profiles: {
      root: '/admin/profiles',
      export: '/admin/profiles/export',
      deactivated: '/admin/profiles/deactivated',
      detail: (id: string) => `/admin/profiles/${id}`,
    },
    api: {
      root: '/admin/api',
      edit: (id: string) => `/admin/api/${id}`,
    },
    enrichment: {
      root: '/admin/enrichment',
      metrics: '/admin/enrichment/metrics',
      detail: (id: string) => `/admin/enrichment/${id}`,
    },
    profileCleaning: '/admin/profile-cleaning',
    /**
     * Exit & Investment Review (#762): the "Exits & investments to complete"
     * queue, a card of Profile Cleaning before. The card's old address,
     * `profileCleaning?type=exits_investments_to_complete`, redirects here.
     */
    exitsInvestments: '/admin/exits-investments',
    profileTypes: '/admin/profile-types',
    nameRules: '/admin/settings/name-rules',
    faq: '/admin/settings/faq',
  },

  /**
   * Legacy URLs kept alive as redirects. Sources are historical paths that
   * external links / old notifications still point at; each maps to its
   * current destination above.
   */
  legacy: {
    profile: '/profile', // → account
    subscription: '/subscription', // → licence
    settings: '/settings', // → account
    urlCemetery: '/admin/url-cemetery', // → admin.profileCleaning
    classification: '/admin/classification', // → admin.profileTypes (#627)
    addLicence: '/admin/licences/add', // → admin.licences.root (a licence is created from the index's New licence dialog only)
    addUser: '/admin/users/add', // → admin.users.root (a user is created from a dialog only)
    editUser: (id: string) => `/admin/users/${id}/edit`, // → admin.users.root (edited from a dialog only)
    suppressedProfiles: '/admin/profiles/suppressed', // → admin.profiles.deactivated (product wording: "suppressed" read as "deleted")
    companies: '/companies', // → institutionals (product wording: the client zone says "Institutionals")
    companyDetail: (id: string) => `/companies/${id}`, // → institutionals.detail
    // → admin.enrichment.* (product wording: the feature is "Enrichment", matching
    // the backend domain). The detail alias matters most: pipeline notifications
    // and emails sent before the rename still point at the old URL.
    dataCollection: '/admin/data-collection',
    dataCollectionMetrics: '/admin/data-collection/metrics',
    dataCollectionDetail: (id: string) => `/admin/data-collection/${id}`,
    // → admin.lists.* (product wording: the admin zone says "Collection", #548).
    // One splat route forwards every sub-path (detail, edit, file-import, …);
    // the two entries below only document the alias for the coverage guard.
    adminLists: '/admin/lists',
    adminListDetail: (id: string) => `/admin/lists/${id}`,
    // → fundraisings.dialog (#668: creating and editing a round are dialogs now).
    fundraisingNew: '/fundraisings/new',
    fundraisingEdit: (id: string) => `/fundraisings/${id}/edit`,
  },
} as const;

export default paths;
